import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard, FolderKanban, FileCheck2, ScrollText, Image as ImageIcon,
  Sparkles, BookOpen, LogOut, Search, Send, Upload, ShieldCheck, AlertTriangle,
  CheckCircle2, ChevronRight, Building2, Hash, BarChart3, MessageSquareText, Loader2, X,
  Inbox, Link as LinkIcon, Menu, LogIn, FileText
} from "lucide-react";
import { API } from "../lib/api";
import { fmtAUD, fmtMoney, fmtBytes } from "../lib/format";

export function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const r = await axios.post(`${API}/auth/login`, { email: email.trim(), name: name.trim() });
      onLogin(r.data); nav("/");
    } catch (e) { setErr(e?.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5" data-testid="login-page">
      <div className="lg:col-span-3 relative hidden lg:block">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?crop=entropy&cs=srgb&fm=jpg&q=85)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(0,38,100,0.92), rgba(0,29,77,0.78))" }} />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-white flex items-center justify-center"><ShieldCheck className="w-6 h-6" style={{color:"#002664"}} /></div>
            <div>
              <div className="font-heading font-bold text-lg leading-none">NSWRA</div>
              <div className="text-[11px] tracking-[0.18em] uppercase opacity-80">Reconstruction Authority</div>
            </div>
          </div>
          <div className="max-w-xl">
            <div className="text-[11px] tracking-[0.2em] uppercase opacity-70 mb-3">DRFA Submission Assessment Tool</div>
            <h1 className="font-heading text-5xl font-bold leading-[1.05] mb-6">Audit-grade DRFA assessment.</h1>
            <p className="text-base opacity-85 leading-relaxed">EW Finalisation, IRW Scope, IRW Finalisation, EPAR Scope and EPAR Finalisation — the tool sorts the data and surfaces decision options. The assessor decides.</p>

            {/* Government-trust strip — visible BEFORE sign-in so NSWRA / NSW Gov reviewers see the security posture up front. */}
            <div className="mt-8 rounded-md border border-white/15 bg-white/5 p-4 text-[11.5px] leading-relaxed opacity-90" data-testid="login-security-strip">
              <div className="text-[10px] tracking-[0.18em] uppercase font-bold mb-1.5 opacity-90">Security &amp; AI transparency</div>
              <ul className="space-y-1">
                <li>· Domain-locked sign-in. Audit trail on every assessor decision.</li>
                <li>· AI is read-only — extracts &amp; classifies; <b>never</b> writes outcomes.</li>
                <li>· The <b>SQP makes every decision</b>. RAAgent records the click.</li>
              </ul>
              <Link to="/security" className="inline-block mt-2 text-[11px] underline underline-offset-2 opacity-90 hover:opacity-100" data-testid="login-security-link">Read the full statement →</Link>
            </div>
          </div>
          <div className="text-[11px] opacity-70 font-mono">© NSW Reconstruction Authority · Internal Assessor Portal</div>
        </div>
      </div>
      <div className="lg:col-span-2 flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="label-upper mb-2">Assessor Sign-in</div>
          <h2 className="font-heading text-2xl font-bold mb-1">Welcome back.</h2>
          <p className="text-sm text-slate-500 mb-8">Sign in to continue your assigned assessments.</p>
          <label className="label-upper mb-1 block">Name</label>
          <input className="input mb-4" value={name} onChange={e=>setName(e.target.value)} placeholder="Your name" data-testid="login-name-input" />
          <label className="label-upper mb-1 block">Email</label>
          <input className="input mb-2" value={email} onChange={e=>setEmail(e.target.value)} type="email" required placeholder="you@yourorganisation.gov.au" data-testid="login-email-input" />
          <div className="text-[11px] text-slate-500 mb-5">Authorised assessors only.</div>
          {err && <div className="mb-4 text-xs text-red-600" data-testid="login-error">{err}</div>}
          <button disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="login-submit-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Sign in to portal
          </button>
          <div className="mt-4 pt-4 border-t border-slate-200 text-center">
            <div className="text-[10.5px] tracking-[0.16em] uppercase text-slate-500 font-semibold mb-1.5">Not signed up yet?</div>
            <Link
              to="/showcase"
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#002664] hover:underline"
              data-testid="login-live-demo-link"
            >
              ▸ Watch the 90-second live demo
            </Link>
          </div>
          <div className="mt-6 text-[11px] text-slate-500 leading-relaxed">RAAgent · internal RHE assessor portal · RHE Project Consulting. <span className="inline-block ml-1 px-1.5 py-0.5 rounded bg-slate-900 text-white text-[9px] tracking-[0.18em] uppercase font-bold align-middle">Powered by RAAgent</span></div>
        </form>
      </div>
    </div>
  );
}
